import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Spin, Input, Button, message, Grid, Card, Typography, Modal } from 'antd';
import IndependentChat from '../components/IndependentChat';
import { createToken, createChatSession, traceApi, chatApi, fetchSetting } from '../services/api';
import { useIntl } from 'react-intl';
import { LoadingOutlined, UserOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { useRef } from 'react';
import ChatClosedPage from '../components/ChatClosedPage';
import { CategoryType } from '../components/CategorySelector';
import CategoryReplyPreview from '../components/CategoryPrevew';
import UCQuestionPage from '../components/uchat/UCQuestionPage';
import type { Category, CategoryConfig, CategoryQuestionNode } from '../types/category';
import { hasQuestionTree, isQuestionBranch } from '../types/categoryQuestionTree';

const { useBreakpoint } = Grid;
const { Text } = Typography;

const ChatPage = () => {
  const screens = useBreakpoint();
  const location = useLocation();
  const [params, setParams] = useState<{
    token: string; // 用户令牌
    code: string;
    category?: string; // 分类信息，现在支持动态分类名称
  }>();
  const [loading, setLoading] = useState(false); // 加载状态
  const [userName, setUserName] = useState(''); // 用户名
  const [formSubmitted, setFormSubmitted] = useState(false); // 表单提交状态

  const intl = useIntl(); // 国际化
  const [code, setCode] = useState(''); // URL中的code参数
  const [token, setToken] = useState(''); // URL中的token参数
  const [uid, setUid] = useState(''); // URL中的uid参数
  const [username, setUsername] = useState(''); // URL中的username参数
  const [hasInitialized, setHasInitialized] = useState(false); // 防止重复初始化
  const [chatClosed, setChatClosed] = useState(false); // 聊天已关闭标记
  const [selectedCategory] = useState<CategoryType | null>(null); // 选中的分类（保留用于兼容性）
  const [showCategorySelector, setShowCategorySelector] = useState(false); // 是否显示分类选择器
  const [categoryData, setCategoryData] = useState<Category | undefined>(undefined); // 从接口获取的分类配置数据
  const [selectedCategoryConfig, setSelectedCategoryConfig] = useState<CategoryConfig | undefined>(undefined); // 选中的分类配置
  const [showQuestionSelector, setShowQuestionSelector] = useState(false);
  const [questionStack, setQuestionStack] = useState<CategoryQuestionNode[][]>([]);
  const superAdminUid = '21232f297a57a5a743894a0e4a801fc3';
  const handleSubmit = useCallback(async () => {
    if (token === null && !userName?.trim()) {
      message.error(intl.formatMessage({ id: 'chat.username_required' }));
      return;
    }

    // 如果没有选择分类，显示分类选择器
    if (!selectedCategory) {
      // 重置埋点引用，以便重新触发"进入确认页"埋点
      confirmTraceSentRef.current = false;
      setShowCategorySelector(true);
      return;
    }

    setLoading(true);
    try {
      let effectiveUserName = token ? null : userName?.trim();
      // 如果是从URL获取的uid，不添加Visitor前缀
      if (effectiveUserName && !uid) {
        effectiveUserName = `Visitor-${Math.floor(Math.random() * 10000)}-${effectiveUserName}`;
      }
      // 如果有选中的分类配置，使用其name作为分类值
      const categoryValue = selectedCategoryConfig ? selectedCategoryConfig.name : undefined;
      // 手动输入时，使用用户输入的值作为uid和username
      const effectiveUid = uid || effectiveUserName || '';
      const effectiveUsername = username || effectiveUserName || '';
      setUid(effectiveUid);
      setUsername(effectiveUsername);
      let response = await createToken(
        code,
        token,
        effectiveUid,
        false,
        categoryValue,
        effectiveUsername,
        superAdminUid
      );
      if (response && response.success === false && response.code === 'C_1') {
        setChatClosed(true);
        return;
      }
      if (response) {
        const record = response.data;

        // 立即调用创建对话接口
        const sessionResponse = await createChatSession(
          record.token,
          record.code,
          record.uid,
          record.username,
          superAdminUid
        );
        if (sessionResponse && sessionResponse.success === false && sessionResponse.code === 'C_1') {
          setChatClosed(true);
          return;
        }

        if (sessionResponse && sessionResponse.success) {
          // 创建会话成功后，调用分类回复接口
          try {
            await chatApi.categoryAnswer(sessionResponse.data.token, selectedCategory);
            console.log('分类回复接口调用成功');
          } catch (error) {
            console.error('分类回复接口调用失败:', error);
            // 不阻止后续流程，继续进入聊天
          }

          setParams({
            token: sessionResponse.data.token,
            code: code,
            category: selectedCategory
          });
          // 覆盖浏览器地址参数为token
          const newUrl = `${window.location.pathname}?token=${sessionResponse.data.token}`;
          window.history.replaceState({}, '', newUrl);
          // 获取快捷问题列表
        } else {
          message.error(intl.formatMessage({ id: 'chat.session.create.failed' }, { defaultMessage: '创建对话失败' }));
          return;
        }
      }
    } catch (error) {
      console.error('初始化失败', error);
      message.error(intl.formatMessage({ id: 'chat.init_failed' }));
    } finally {
      setLoading(false);
      setFormSubmitted(true);
    }
  }, [code, token, userName, uid, intl, selectedCategory, selectedCategoryConfig, username]);

  // 处理CategoryPreview的分类选择
  const handleCategoryConfigSelect = useCallback(
    async (config: CategoryConfig, questionLeafId?: string, skipQuestionTree?: boolean) => {
      setSelectedCategoryConfig(config);
      setShowCategorySelector(false);

      setLoading(true);
      let stayOnQuestion = false;
      try {
        // 如果已经有params，说明是切换分类
        if (params) {
          try {
            await chatApi.categoryAnswerV2(params.token, config.name);
            setParams({
              ...params,
              category: config.name as any
            });
            // 重置埋点引用，以便重新触发"进入会话页"埋点
            conversationTraceSentRef.current = false;
            // 触发"进入会话页"埋点
            try {
              traceApi.inConversation({
                code: params.code,
                uuid: params.token
              });
              conversationTraceSentRef.current = true;
            } catch (e) {
              // ignore trace error
            }
            message.success(intl.formatMessage({ id: 'category.switch.success' }, { defaultMessage: '分类切换成功' }));
          } catch (error) {
            console.error('分类切换失败:', error);
            message.error(intl.formatMessage({ id: 'category.switch.failed' }, { defaultMessage: '分类切换失败' }));
          }
        } else {
          // Other：跳过选题，按无问题树流程直接建会话
          if (!skipQuestionTree && !questionLeafId && hasQuestionTree(config)) {
            setQuestionStack([config.questionTree || []]);
            setShowQuestionSelector(true);
            stayOnQuestion = true;
            return;
          }
          // 新建会话流程
          if (token === null && !userName?.trim()) {
            message.error(intl.formatMessage({ id: 'chat.username_required' }));
            return;
          }

          let effectiveUserName = token ? null : userName?.trim();
          // 如果是从URL获取的uid，不添加Visitor前缀
          if (effectiveUserName && !uid) {
            effectiveUserName = `Visitor-${Math.floor(Math.random() * 10000)}-${effectiveUserName}`;
          }
          // 手动输入时，使用用户输入的值作为uid和username
          const effectiveUid = uid || effectiveUserName || '';
          const effectiveUsername = username || effectiveUserName || '';
          let response = await createToken(
            code,
            token,
            effectiveUid,
            false,
            config.name,
            effectiveUsername,
            superAdminUid
          );
          if (response && response.success === false && response.code === 'C_1') {
            setChatClosed(true);
            return;
          }
          if (response) {
            const record = response.data;

            // 立即调用创建对话接口
            const sessionResponse = await createChatSession(
              record.token,
              record.code,
              record.uid,
              record.username,
              superAdminUid
            );
            if (sessionResponse && sessionResponse.success === false && sessionResponse.code === 'C_1') {
              setChatClosed(true);
              return;
            }

            if (sessionResponse && sessionResponse.success) {
              // 创建会话成功后，调用分类回复接口，使用config.name
              try {
                await chatApi.categoryAnswerV2(sessionResponse.data.token, config.name);
                if (questionLeafId) {
                  await chatApi.questionTreeAnswer({
                    token: sessionResponse.data.token,
                    category: config.name,
                    questionLeafId
                  });
                }
                console.log('分类回复接口调用成功');
              } catch (error) {
                console.error('分类回复接口调用失败:', error);
                // 不阻止后续流程，继续进入聊天
              }

              setShowQuestionSelector(false);
              setQuestionStack([]);
              setParams({
                token: sessionResponse.data.token,
                code: code,
                category: config.name as any // 直接使用config.name
              });
              // 重置埋点引用，以便重新触发"进入会话页"埋点
              conversationTraceSentRef.current = false;
              // 触发"进入会话页"埋点
              try {
                traceApi.inConversation({
                  code: code,
                  uuid: sessionResponse.data.token
                });
                conversationTraceSentRef.current = true;
              } catch (e) {
                // ignore trace error
              }
              // 覆盖浏览器地址参数为token
              const newUrl = `${window.location.pathname}?token=${sessionResponse.data.token}`;
              window.history.replaceState({}, '', newUrl);
            } else {
              message.error(
                intl.formatMessage({ id: 'chat.session.create.failed' }, { defaultMessage: '创建对话失败' })
              );
              return;
            }
          }
        }
      } catch (error) {
        console.error('初始化失败', error);
        message.error(intl.formatMessage({ id: 'chat.init_failed' }));
      } finally {
        setLoading(false);
        if (!stayOnQuestion) {
          setFormSubmitted(true);
        }
      }
    },
    [code, token, userName, uid, intl, username, params]
  );

  const handleQuestionSelect = useCallback(
    (node: CategoryQuestionNode) => {
      if (isQuestionBranch(node)) {
        setQuestionStack(prev => [...prev, node.children || []]);
        return;
      }
      if (selectedCategoryConfig) {
        handleCategoryConfigSelect(selectedCategoryConfig, node.id);
      }
    },
    [selectedCategoryConfig, handleCategoryConfigSelect]
  );

  const handleQuestionOther = useCallback(() => {
    if (selectedCategoryConfig) {
      handleCategoryConfigSelect(selectedCategoryConfig, undefined, true);
    }
  }, [selectedCategoryConfig, handleCategoryConfigSelect]);

  const handleQuestionBack = useCallback(() => {
    setQuestionStack(prev => {
      if (prev.length <= 1) {
        setShowQuestionSelector(false);
        setShowCategorySelector(true);
        return [];
      }
      return prev.slice(0, -1);
    });
  }, []);

  // 获取分类显示名称
  const getCategoryDisplayName = useCallback(
    (category: string) => {
      // 如果是动态分类名称，直接返回
      if (typeof category === 'string') {
        return category;
      }
      // 兼容旧的CategoryType
      const categoryNames = {
        DEPOSIT: intl.formatMessage({ id: 'category.deposit' }, { defaultMessage: '充值' }),
        WITHDRAW: intl.formatMessage({ id: 'category.withdraw' }, { defaultMessage: '提现' }),
        LOGIN_OTP: intl.formatMessage({ id: 'category.login_otp' }, { defaultMessage: '登录/OTP' }),
        KYC: intl.formatMessage({ id: 'category.kyc' }, { defaultMessage: 'KYC' }),
        WELFARE_OTHER: intl.formatMessage({ id: 'category.welfare_other' }, { defaultMessage: '福利及其他' })
      };
      return categoryNames[category as CategoryType] || category;
    },
    [intl]
  );

  // 埋点：进入确认页（仅触发一次）
  const confirmTraceSentRef = useRef(false);
  useEffect(() => {
    const inConfirmPage = showCategorySelector || (!params && !formSubmitted && (!token || uid));
    if (inConfirmPage && !confirmTraceSentRef.current) {
      confirmTraceSentRef.current = true;
      try {
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        traceApi.toConfirmPage({ code, uid });
      } catch (e) {
        // ignore trace error
      }
    }
  }, [showCategorySelector, params, formSubmitted, token, uid, code, selectedCategory, location.search]);

  // 埋点：进入会话页（仅触发一次）
  const conversationTraceSentRef = useRef(false);
  useEffect(() => {
    if (params && !conversationTraceSentRef.current) {
      conversationTraceSentRef.current = true;
      try {
        traceApi.inConversation({ code, uuid: params.token });
      } catch (e) {
        // ignore trace error
      }
    }
  }, [params, code, selectedCategory]);

  // 获取设置数据
  useEffect(() => {
    const fetchCategoryData = async () => {
      if (code) {
        try {
          const response = await fetchSetting(code);
          if (response && response.success && response.data && response.data.category) {
            // 如果category是字符串，需要解析JSON
            let categoryData;
            if (typeof response.data.category === 'string') {
              try {
                categoryData = JSON.parse(response.data.category);
              } catch (parseError) {
                console.error('解析分类配置JSON失败:', parseError);
                return;
              }
            } else {
              categoryData = response.data.category;
            }
            setCategoryData(categoryData);
          }
        } catch (error) {
          console.error('获取分类配置失败:', error);
        }
      }
    };

    fetchCategoryData();
  }, [code]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCode = searchParams.get('code');
    const urlToken = searchParams.get('token');
    const urlUid = searchParams.get('uid');
    const urlUsername = searchParams.get('username');

    setCode(urlCode || '');
    setToken(urlToken || '');
    setUid(urlUid || '');
    setUsername(urlUsername || '');

    // 优先使用username，其次使用uid预填充到用户名输入框
    if (urlUsername) {
      setUserName(urlUsername);
    } else if (urlUid) {
      setUserName(urlUid);
    }

    // 如果有token，直接创建会话并获取分类信息
    if (urlToken && !hasInitialized) {
      (async () => {
        try {
          setLoading(true);

          // 先获取会话信息
          const sessionResponse = await createChatSession(urlToken);

          // 处理会话响应
          if (sessionResponse && sessionResponse.success === false && sessionResponse.code === 'C_1') {
            setChatClosed(true);
            return;
          }

          if (sessionResponse && sessionResponse.success) {
            // 从会话响应中获取当前分类和code
            const currentCategory = sessionResponse.data.category;
            const sessionCode = sessionResponse.data.code || urlCode; // 优先使用返回的code，否则使用URL中的code

            // 设置会话参数
            setParams({
              token: sessionResponse.data.token,
              code: sessionCode,
              category: currentCategory
            });

            // 更新code状态
            setCode(sessionCode);

            // 使用会话返回的code获取设置
            const settingResponse = await fetchSetting(sessionCode);

            // 处理分类配置响应
            if (settingResponse && settingResponse.success && settingResponse.data && settingResponse.data.category) {
              let categoryData;
              if (typeof settingResponse.data.category === 'string') {
                try {
                  categoryData = JSON.parse(settingResponse.data.category);
                } catch (parseError) {
                  console.error('解析分类配置JSON失败:', parseError);
                }
              } else {
                categoryData = settingResponse.data.category;
              }
              setCategoryData(categoryData);

              // 如果有当前分类，找到对应的配置并设置
              if (currentCategory && categoryData?.configs) {
                const currentConfig = categoryData.configs.find(
                  (config: CategoryConfig) => config.name === currentCategory
                );
                if (currentConfig) {
                  setSelectedCategoryConfig(currentConfig);
                }
              }
            }
          } else {
            message.error(intl.formatMessage({ id: 'chat.session.create.failed' }, { defaultMessage: '创建对话失败' }));
          }
        } catch (error) {
          console.error('初始化失败', error);
          message.error(intl.formatMessage({ id: 'chat.init_failed' }));
        } finally {
          setLoading(false);
          setHasInitialized(true);
        }
      })();
    }
  }, [location.search, hasInitialized, intl]);

  // 响应式布局参数
  const responsiveStyles = {
    container: {
      padding: screens.xs ? '24px 12px' : '40px 20px',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    },
    formContainer: {
      width: '100%',
      maxWidth: screens.xs ? '100%' : '580px',
      padding: screens.xs ? '24px' : '40px',
      borderRadius: '16px'
    },
    input: {
      height: screens.xs ? '40px' : '44px',
      fontSize: screens.xs ? '14px' : '16px'
    },
    button: {
      width: screens.xs ? '100%' : '200px',
      height: screens.xs ? '40px' : '44px',
      fontSize: screens.xs ? '14px' : '16px'
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        margin: 0,
        ...responsiveStyles.container
      }}
    >
      {chatClosed ? (
        <ChatClosedPage />
      ) : (
        <Modal
          open={showCategorySelector}
          onCancel={() => setShowCategorySelector(false)}
          footer={null}
          width={800}
          destroyOnClose
          centered
          title={null}
          bodyStyle={{
            padding: '32px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}
          style={{
            maxWidth: '95vw'
          }}
          className="category-selector-modal"
        >
          <CategoryReplyPreview
            categoryData={categoryData}
            onCategorySelect={handleCategoryConfigSelect}
            selectedCategory={selectedCategoryConfig}
          />
        </Modal>
      )}
      {showQuestionSelector && selectedCategoryConfig ? (
        <UCQuestionPage
          categoryConfig={selectedCategoryConfig}
          options={questionStack[questionStack.length - 1] || []}
          colors={{ background: '#fff', text: '#333', cardBg: '#f5f5f5' }}
          onBack={handleQuestionBack}
          onSelect={handleQuestionSelect}
          onSelectOther={handleQuestionOther}
        />
      ) : null}
      {!params && !formSubmitted && !showQuestionSelector && (!token || uid) ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}
        >
          <div
            style={{
              ...responsiveStyles.formContainer,
              background: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h2
              style={{
                marginBottom: '24px',
                color: '#1890ff',
                fontWeight: 500,
                fontSize: screens.xs ? '20px' : '24px',
                textAlign: 'center'
              }}
            >
              {/* {intl.formatMessage({ id: 'chat.welcome' })+' '+kb.name} */}
            </h2>

            {/* 显示已选择的分类 */}
            {selectedCategory && (
              <Card
                size="small"
                style={{
                  marginBottom: '16px',
                  backgroundColor: '#f6ffed',
                  border: '1px solid #b7eb8f'
                }}
                bodyStyle={{ padding: '12px 16px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <Text style={{ color: '#52c41a', fontWeight: 500 }}>
                      {intl.formatMessage({ id: 'category.selected' }, { defaultMessage: '已选择：' })}
                      {getCategoryDisplayName(selectedCategory)}
                    </Text>
                  </div>
                </div>
              </Card>
            )}

            <Input
              prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
              placeholder={intl.formatMessage({ id: 'chat.input_placeholder' })}
              value={userName}
              onChange={e => setUserName(e.target.value)}
              disabled={!!uid} // 当有uid时禁用输入框
              style={{
                ...responsiveStyles.input,
                width: '100%',
                marginBottom: '24px',
                borderRadius: '8px',
                backgroundColor: uid ? '#f5f5f5' : undefined, // 禁用时显示灰色背景
                cursor: uid ? 'not-allowed' : undefined
              }}
              onPressEnter={() => handleSubmit()}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: screens.xs ? 'center' : 'flex-end'
              }}
            >
              <Button
                type="primary"
                shape="round"
                onClick={() => handleSubmit()}
                loading={loading}
                style={responsiveStyles.button}
                disabled={!userName?.trim()}
              >
                {intl.formatMessage({ id: 'chat.start_chat' })}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(3px)'
          }}
        >
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
            tip={intl.formatMessage({ id: 'chat.initializing' })}
          />
        </div>
      )}

      {params && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            minHeight: 'calc(100vh - 80px)',
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: screens.xs ? '0' : '20px 0'
          }}
        >
          <div
            style={{
              flex: 1,
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              margin: screens.xs ? 0 : '0 20px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* 聊天头部 - 显示分类信息 */}
            {params.category && (
              <div
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #e8e8e8',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <div>
                    <Text style={{ color: '#666', fontSize: '14px' }}>
                      {intl.formatMessage({ id: 'consultation.type' })}
                    </Text>
                    <Text
                      style={{
                        color: '#1890ff',
                        fontWeight: 500,
                        marginLeft: '8px'
                      }}
                    >
                      {getCategoryDisplayName(params.category)}
                    </Text>
                  </div>
                  <Button
                    type="link"
                    onClick={() => {
                      // 重置埋点引用，以便重新触发"进入确认页"埋点
                      confirmTraceSentRef.current = false;
                      setShowCategorySelector(true);
                    }}
                    style={{ padding: '4px 8px' }}
                  >
                    {intl.formatMessage({ id: 'category.switch' }, { defaultMessage: '切换分类' })}
                  </Button>
                </div>
              </div>
            )}

            {/* 聊天组件 */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {(() => {
                return <IndependentChat token={params.token} code={params.code} />;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
